import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLogto } from '@logto/react';
import {
  Rocket,
  RefreshCw,
  Check,
  FolderOpen,
  Loader2,
  Save,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { MMMConfigFormState, DEFAULT_FORM_STATE, MMMConfig, DataFrequency, OptimizerType } from '../types/mmmConfig';
import { validateMMMConfig } from '../utils/validation';
import { configService } from '../services/configService';
import { storageService } from '../services/storageService';
import { SavedConfigsBrowser } from './SavedConfigsBrowser';
import { DiagnosticAssistantPanel } from './DiagnosticAssistantPanel';
import { useDiagnostics } from '../hooks/useDiagnostics';
import {
  GlobalSettingsSection,
  MediaChannelsSection,
  ControlVariablesSection,
  FeatureEngineeringSection,
  CalendarAndSeasonalitySection,
  SmartColumnMappingSection,
} from './Sections';

interface ConfigFormProps {
  dataFileId?: string;
  dataColumns: string[];
  onSuccess?: (configId: string) => void;
  onAnalysisStart?: (jobId: string) => void;
}

export const ConfigForm: React.FC<ConfigFormProps> = ({
  dataFileId,
  dataColumns,
  onAnalysisStart
}) => {
  const [config, setConfig] = useState<MMMConfigFormState>(DEFAULT_FORM_STATE);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [analysisStatus, setAnalysisStatus] = useState<'idle' | 'running' | 'completed' | 'error'>('idle');
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [analysisProgress, setAnalysisProgress] = useState<number>(0);
  const [showSavedConfigs, setShowSavedConfigs] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const logto = useLogto();
  const [preTrainingReport, setPreTrainingReport] = useState<any>(null);
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  const [showOptimizationSettings, setShowOptimizationSettings] = useState(false);
  const { runPreTrainingCheck, applyQuickFix, isApplyingFix } = useDiagnostics();

  // Refs for managing polling lifecycle to prevent memory leaks
  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const currentJobIdRef = useRef<string | null>(null);
  const isMountedRef = useRef(true);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
    };
  }, []);

  // Validate on changes
  useEffect(() => {
    const validationErrors = validateMMMConfig(config, dataColumns);
    const errorMap: Record<string, string> = {};
    validationErrors.forEach((err) => {
      if (!errorMap[err.field]) {
        errorMap[err.field] = err.message;
      }
    });
    setErrors(errorMap);
  }, [config, dataColumns]);

  // Auto-detect frequency when date variable changes
  useEffect(() => {
    const detectFreq = async () => {
      if (config.date_var && dataFileId) {
        console.log("Detecting frequency for", config.date_var);
        const result = await configService.detectFrequency(logto, dataFileId, config.date_var);
        if (result.success && result.frequency) {
          console.log("Detected frequency:", result.frequency);
          setConfig(prev => ({ ...prev, frequency: result.frequency as DataFrequency }));
        }
      }
    };

    detectFreq();
  }, [config.date_var, dataFileId]);

  const handleConfigChange = (updates: Partial<MMMConfigFormState>) => {
    setConfig((prev) => ({ ...prev, ...updates }));
  };

  const handlePreTrainingValidation = async () => {
    if (!dataFileId) {
      setSaveError('No dataset selected');
      return;
    }

    try {
      setSaveStatus('saving');
      const report = await runPreTrainingCheck(dataFileId, config);
      setPreTrainingReport(report);
      setShowDiagnostics(true);

      if (report.errors_count > 0) {
        setSaveStatus('error');
        setSaveError(`Pre-training validation failed: ${report.errors_count} errors detected`);
      } else {
        setSaveStatus('saved');
        if (report.warnings_count > 0) {
          setSaveError(`${report.warnings_count} warnings detected - review recommended`);
        }
        setTimeout(() => setSaveStatus('idle'), 2000);
      }
    } catch (err) {
      setSaveStatus('error');
      setSaveError(err instanceof Error ? err.message : 'Pre-training validation error');
      console.error('Pre-training validation error:', err);
    }
  };

  const handleApplyFix = async (fixAction: string, params?: any) => {
    try {
      const result = await applyQuickFix(dataFileId!, config, fixAction, params);

      // Check if component is still mounted after async operation
      if (!isMountedRef.current) return;

      if (result.success && result.updated_config) {
        setConfig(result.updated_config);
        await handlePreTrainingValidation();
        if (!isMountedRef.current) return;
        setSaveStatus('saved');
        setSaveError(null);
      }
    } catch (err) {
      if (!isMountedRef.current) return;
      console.error('Quick-fix failed:', err);
      setSaveStatus('error');
      setSaveError(err instanceof Error ? err.message : 'Quick-fix failed');
    }
  };

  const handleReset = () => {
    setConfig(DEFAULT_FORM_STATE);
    setErrors({});
    setSaveStatus('idle');
    setSaveError(null);
  };

  const handleSaveConfigForReuse = async () => {
    if (Object.keys(errors).length > 0) {
      setSaveError('Please fix validation errors before saving');
      return;
    }

    const configName = window.prompt(
      'Enter a name for this configuration:',
      `config_${new Date().toISOString().split('T')[0]}`
    );
    if (!configName) return;

    const description = window.prompt('Enter a description (optional):', '');

    setSavingConfig(true);
    try {
      const result = await storageService.saveConfig(
        logto,
        config as MMMConfig,
        configName,
        description || undefined
      );
      if (result.success) {
        alert(`Configuration "${configName}" saved successfully!`);
      } else {
        alert(`Failed to save configuration: ${result.message}`);
      }
    } catch (error) {
      alert(`Error saving configuration: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSavingConfig(false);
    }
  };

  const handleLoadSavedConfig = (loadedConfig: MMMConfig, configName: string) => {
    setShowSavedConfigs(false);
    setConfig({ ...loadedConfig });
    alert(`Loaded configuration: ${configName}`);
  };

  // Memoized polling function with proper cleanup
  const pollAnalysisStatus = useCallback(async (id: string) => {
    // Check if this polling request is still valid
    if (!isMountedRef.current || currentJobIdRef.current !== id) {
      return; // Component unmounted or job changed, stop polling
    }

    try {
      const response = await configService.checkAnalysisStatus(logto, id);

      // Double-check after async call
      if (!isMountedRef.current || currentJobIdRef.current !== id) {
        return;
      }

      if (response.status === 'completed') {
        setAnalysisStatus('completed');
        setAnalysisProgress(100);
        currentJobIdRef.current = null; // Clear job tracking
        // Trigger callback to navigate to results view
        if (onAnalysisStart) {
          setTimeout(() => onAnalysisStart(id), 1000);
        }
      } else if (response.status === 'failed' || response.status === 'error') {
        setAnalysisStatus('error');
        setAnalysisError(response.error || 'Analysis failed');
        currentJobIdRef.current = null; // Clear job tracking
      } else if (response.status === 'running' || response.status === 'pending') {
        setAnalysisProgress(response.progress || 0);
        // Continue polling with proper timeout tracking
        pollingTimeoutRef.current = setTimeout(() => pollAnalysisStatus(id), 2000);
      }
    } catch (err) {
      if (!isMountedRef.current) return;
      setAnalysisStatus('error');
      setAnalysisError(err instanceof Error ? err.message : 'Failed to check analysis status');
      currentJobIdRef.current = null;
    }
  }, [onAnalysisStart]);

  const handleRunAnalysis = async () => {
    // First save the configuration
    if (Object.keys(errors).length > 0) {
      setSaveError('Please fix validation errors before running analysis');
      return;
    }

    if (!dataFileId) {
      setAnalysisError('Data file ID is required');
      return;
    }

    // Cancel any existing polling (prevents race condition)
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
    currentJobIdRef.current = null;

    try {
      setAnalysisStatus('running');
      setAnalysisError(null);
      setAnalysisProgress(10);

      const result = await configService.runAnalysis(logto, config, dataFileId);

      if (result.success && result.job_id) {
        setJobId(result.job_id);
        setAnalysisProgress(20);
        // Track this job and start polling
        currentJobIdRef.current = result.job_id;
        pollAnalysisStatus(result.job_id);
      } else {
        setAnalysisStatus('error');
        setAnalysisError(result.message || 'Failed to start analysis');
      }
    } catch (err) {
      setAnalysisStatus('error');
      setAnalysisError(err instanceof Error ? err.message : 'Analysis error');
    }
  };

  const isValid = Object.keys(errors).length === 0;
  const mediaChannelsError = errors['media_channels'];

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-10">
        <h1 className="text-3xl font-bold text-slate-900 mb-3 tracking-tight">
          Model Configuration
        </h1>
        <p className="text-lg text-slate-600">
          Define your marketing channels, control variables, and optimization parameters.
        </p>
      </div>

      {/* Status Messages */}
      {saveError && (
        <div className="mb-8 p-5 bg-gradient-to-r from-rose-50 to-red-50 border border-rose-200 rounded-xl">
          <p className="text-rose-800 font-semibold mb-1">Configuration Error</p>
          <p className="text-rose-700 text-sm">{saveError}</p>
        </div>
      )}

      {saveStatus === 'saved' && (
        <div className="mb-8 p-5 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl">
          <p className="text-emerald-800 font-semibold mb-1">Configuration Saved</p>
          <p className="text-emerald-700 text-sm">Your configuration has been saved successfully.</p>
        </div>
      )}

      {/* Diagnostic Assistant Panel */}
      {showDiagnostics && preTrainingReport && (
        <DiagnosticAssistantPanel
          issues={preTrainingReport.issues || []}
          recommendations={preTrainingReport.recommendations || []}
          onApplyFix={handleApplyFix}
          isPreTraining={true}
        />
      )}

      {/* Mode Toggle */}
      <div className="flex justify-end mb-8">
        <div className="flex items-center gap-4 px-5 py-3 bg-white rounded-xl border border-slate-200 shadow-sm">
          <span className={`text-sm font-medium transition-colors ${!config.isAdvancedMode ? 'text-violet-600' : 'text-slate-400'}`}>
            Basic
          </span>
          <button
            onClick={() => handleConfigChange({ isAdvancedMode: !config.isAdvancedMode })}
            className={`
              relative inline-flex h-7 w-12 flex-shrink-0 cursor-pointer rounded-full
              transition-colors duration-200 ease-out
              ${config.isAdvancedMode ? 'bg-violet-600' : 'bg-slate-200'}
            `}
          >
            <span
              className={`
                pointer-events-none inline-block h-5 w-5 mt-1 ml-1 transform rounded-full bg-white
                shadow-md ring-0 transition-transform duration-200 ease-out
                ${config.isAdvancedMode ? 'translate-x-5' : 'translate-x-0'}
              `}
            />
          </button>
          <span className={`text-sm font-medium transition-colors ${config.isAdvancedMode ? 'text-violet-600' : 'text-slate-400'}`}>
            Advanced
          </span>
        </div>
      </div>

      {/* Sections */}
      <GlobalSettingsSection
        config={config}
        dataColumns={dataColumns}
        onChange={handleConfigChange}
        errors={errors}
        isAdvancedMode={config.isAdvancedMode}
      />

      {!config.isAdvancedMode ? (
        <SmartColumnMappingSection
          config={config}
          dataColumns={dataColumns}
          onChange={handleConfigChange}
        />
      ) : (
        <>
          <MediaChannelsSection
            config={config}
            dataColumns={dataColumns}
            onChange={handleConfigChange}
            errors={errors}
          />

          {mediaChannelsError && (
            <div className="mb-6 p-4 bg-yellow-50 border-l-4 border-yellow-500 rounded">
              <p className="text-yellow-800 text-sm">{mediaChannelsError}</p>
            </div>
          )}

          <ControlVariablesSection
            config={config}
            dataColumns={dataColumns}
            onChange={handleConfigChange}
            errors={errors}
          />

          <FeatureEngineeringSection config={config} onChange={handleConfigChange} errors={errors} />

          <CalendarAndSeasonalitySection
            config={config}
            onChange={handleConfigChange}
            errors={errors}
          />

          {/* Optimization Settings Section - Collapsible */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm mb-8 hover:shadow-lg transition-shadow overflow-hidden">
            <button
              type="button"
              onClick={() => setShowOptimizationSettings(!showOptimizationSettings)}
              className="w-full p-6 flex items-center justify-between hover:bg-slate-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg shadow-violet-500/25">
                  <Rocket className="w-5 h-5 text-white" />
                </div>
                <div className="text-left">
                  <h2 className="text-xl font-bold text-slate-900">Optimization Settings</h2>
                  <p className="text-sm text-slate-500">
                    {showOptimizationSettings
                      ? 'Configure hyperparameter search behavior'
                      : `${config.optimization?.iterations_per_trial || 2000} iterations × ${config.optimization?.num_trials || 1} trial(s)`}
                  </p>
                </div>
              </div>
              {showOptimizationSettings ? (
                <ChevronDown className="w-5 h-5 text-slate-400" />
              ) : (
                <ChevronRight className="w-5 h-5 text-slate-400" />
              )}
            </button>

            {showOptimizationSettings && (
              <div className="px-8 pb-8 space-y-6">
                {/* Trial & Iteration Controls */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Number of Trials */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Number of Trials (Runs)
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="1000"
                      step="1"
                      value={config.optimization?.num_trials || 1}
                      onChange={(e) => {
                        const numTrials = parseInt(e.target.value) || 1;
                        const itersPerTrial = config.optimization?.iterations_per_trial || 2000;
                        handleConfigChange({
                          optimization: {
                            ...config.optimization,
                            num_trials: numTrials,
                            budget: numTrials * itersPerTrial,
                          },
                        });
                      }}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Independent optimization runs. Default: 1
                    </p>
                    <p className="text-xs text-purple-600 font-semibold mt-2">Example: 5 trials with 2000 iterations = 10,000 total evaluations</p>
                  </div>

                  {/* Iterations Per Trial */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Iterations per Trial
                    </label>
                    <input
                      type="number"
                      min="10"
                      max="1000000"
                      step="10"
                      value={config.optimization?.iterations_per_trial || 2000}
                      onChange={(e) => {
                        const itersPerTrial = parseInt(e.target.value) || 2000;
                        const numTrials = config.optimization?.num_trials || 1;
                        handleConfigChange({
                          optimization: {
                            ...config.optimization,
                            iterations_per_trial: itersPerTrial,
                            budget: numTrials * itersPerTrial,
                          },
                        });
                      }}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Function evaluations per run. Default: 2000
                    </p>
                    <p className="text-xs text-blue-600 font-semibold mt-2">Higher values = better optimization, longer runtime</p>
                  </div>

                  {/* Total Budget (Calculated) */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Total Evaluations
                    </label>
                    <div className="w-full px-4 py-2 border-2 border-purple-300 rounded-lg bg-purple-50 flex items-center justify-center h-11">
                      <span className="text-lg font-bold text-purple-900">
                        {((config.optimization?.num_trials || 1) * (config.optimization?.iterations_per_trial || 2000)).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {config.optimization?.num_trials || 1} trials × {config.optimization?.iterations_per_trial || 2000} iterations
                    </p>
                  </div>
                </div>

                {/* Optimizer Selection */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Optimizer Algorithm
                    </label>
                    <select
                      value={config.optimization?.optimizer || "OnePlusOne"}
                      onChange={(e) =>
                        handleConfigChange({
                          optimization: {
                            ...config.optimization,
                            optimizer: e.target.value as OptimizerType,
                          },
                        })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="OnePlusOne">OnePlusOne (Fast & Effective)</option>
                      <option value="TwoPointsDE">TwoPointsDE (Higher Quality, Slower)</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      OnePlusOne recommended for most cases
                    </p>
                  </div>

                  {/* Multi-Start Strategy Info */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                      <RefreshCw className="w-4 h-4" />
                      Multi-Start Strategy
                    </h4>
                    <p className="text-xs text-blue-800">
                      Multiple trials help escape local optima. Each trial starts from a different random point and optimizes independently.
                      Recommended: 3-5 trials for robust results.
                    </p>
                  </div>
                </div>

                {/* Objective Weights */}
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h3 className="font-semibold text-gray-700 mb-4">Objective Weights (Multi-Objective Tuning)</h3>
                  <p className="text-xs text-gray-500 mb-4">
                    Adjust how much each metric influences optimization. Higher weight = more importance.
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* NRMSE Weight */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        NRMSE Weight
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="10"
                        step="0.1"
                        value={config.optimization?.objective_weights?.nrmse || 1.0}
                        onChange={(e) =>
                          handleConfigChange({
                            optimization: {
                              ...config.optimization,
                              objective_weights: {
                                ...config.optimization?.objective_weights,
                                nrmse: parseFloat(e.target.value) || 1.0,
                              },
                            },
                          })
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">Prediction accuracy</p>
                    </div>

                    {/* DECOMP.RSSD Weight */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        DECOMP.RSSD Weight
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="10"
                        step="0.1"
                        value={config.optimization?.objective_weights?.decomp_rssd || 1.0}
                        onChange={(e) =>
                          handleConfigChange({
                            optimization: {
                              ...config.optimization,
                              objective_weights: {
                                ...config.optimization?.objective_weights,
                                decomp_rssd: parseFloat(e.target.value) || 1.0,
                              },
                            },
                          })
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">Business realism</p>
                    </div>

                    {/* MAPE.LIFT Weight */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        MAPE.LIFT Weight
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="10"
                        step="0.1"
                        value={config.optimization?.objective_weights?.mape_lift || 0.0}
                        onChange={(e) =>
                          handleConfigChange({
                            optimization: {
                              ...config.optimization,
                              objective_weights: {
                                ...config.optimization?.objective_weights,
                                mape_lift: parseFloat(e.target.value) || 0.0,
                              },
                            },
                          })
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">Lift test calibration</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Form Summary */}
      <div className="bg-gradient-to-br from-slate-50 to-slate-100 p-8 rounded-2xl border border-slate-200 mb-8">
        <h2 className="text-lg font-bold mb-6 text-slate-900">Configuration Summary</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
          <div className="space-y-1">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Target Variable</span>
            <p className="text-base font-semibold text-slate-900">{config.dep_var || '—'}</p>
          </div>
          <div className="space-y-1">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Date Column</span>
            <p className="text-base font-semibold text-slate-900">{config.date_var || '—'}</p>
          </div>
          <div className="space-y-1">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Frequency</span>
            <p className="text-base font-semibold text-slate-900">{config.frequency || 'Weekly'}</p>
          </div>
          <div className="space-y-1">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Media Channels</span>
            <p className="text-base font-semibold text-slate-900">{config.media_channels?.length || 0} configured</p>
          </div>
          <div className="space-y-1">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Control Variables</span>
            <p className="text-base font-semibold text-slate-900">{config.control_vars?.length || 0} added</p>
          </div>
          <div className="space-y-1">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Validation</span>
            <p className={`text-base font-semibold ${isValid ? 'text-emerald-600' : 'text-rose-600'}`}>
              {isValid ? 'Ready' : `${Object.keys(errors).length} errors`}
            </p>
          </div>
        </div>
      </div>

      {/* Analysis Progress */}
      {analysisStatus !== 'idle' && (
        <div className={`mb-8 p-6 rounded-2xl border-2 transition-all duration-300 ${analysisStatus === 'running'
          ? 'bg-gradient-to-br from-violet-50 via-purple-50 to-fuchsia-50 border-violet-200'
          : analysisStatus === 'completed'
            ? 'bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200'
            : 'bg-gradient-to-br from-rose-50 to-red-50 border-rose-200'
          }`}>
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-4">
              {/* Animated Icon */}
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg ${analysisStatus === 'running'
                ? 'bg-gradient-to-br from-violet-500 to-purple-600 shadow-violet-500/30'
                : analysisStatus === 'completed'
                  ? 'bg-gradient-to-br from-emerald-500 to-teal-600 shadow-emerald-500/30'
                  : 'bg-gradient-to-br from-rose-500 to-red-600 shadow-rose-500/30'
                }`}>
                {analysisStatus === 'running' && (
                  <Loader2 className="w-7 h-7 text-white animate-spin" />
                )}
                {analysisStatus === 'completed' && (
                  <Check className="w-7 h-7 text-white" />
                )}
                {analysisStatus === 'error' && (
                  <span className="text-white text-xl font-bold">!</span>
                )}
              </div>

              <div>
                <h3 className={`text-xl font-bold ${analysisStatus === 'running' ? 'text-violet-900'
                  : analysisStatus === 'completed' ? 'text-emerald-900'
                    : 'text-rose-900'
                  }`}>
                  {analysisStatus === 'running' && 'Running Analysis...'}
                  {analysisStatus === 'completed' && 'Analysis Complete'}
                  {analysisStatus === 'error' && 'Analysis Failed'}
                </h3>
                <p className={`text-sm mt-1 ${analysisStatus === 'running' ? 'text-violet-600'
                  : analysisStatus === 'completed' ? 'text-emerald-600'
                    : 'text-rose-600'
                  }`}>
                  {analysisStatus === 'running' && 'Optimizing hyperparameters and training model...'}
                  {analysisStatus === 'completed' && 'Your results are ready to view'}
                  {analysisStatus === 'error' && analysisError}
                </p>
              </div>
            </div>

            {/* Job ID Badge */}
            {jobId && (
              <div className="flex-shrink-0">
                <span className="text-xs font-medium text-slate-500 block mb-1">Job ID</span>
                <code className={`text-xs font-mono px-3 py-1.5 rounded-lg ${analysisStatus === 'running' ? 'bg-violet-100 text-violet-700'
                  : analysisStatus === 'completed' ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-rose-100 text-rose-700'
                  }`}>
                  {jobId}
                </code>
              </div>
            )}
          </div>

          {/* Progress Bar */}
          {analysisStatus === 'running' && (
            <div className="mt-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-violet-700">Progress</span>
                <span className="text-sm font-bold text-violet-900">{analysisProgress}%</span>
              </div>
              <div className="w-full bg-violet-100 rounded-full h-3 overflow-hidden">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-violet-500 via-purple-500 to-fuchsia-500 transition-all duration-500 ease-out relative"
                  style={{ width: `${analysisProgress}%` }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-pulse" />
                </div>
              </div>
              <div className="flex items-center gap-2 mt-3">
                <div className="w-2 h-2 rounded-full bg-violet-500 animate-pulse" />
                <span className="text-xs text-violet-600">This may take a few minutes depending on your configuration</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Saved Configurations Browser */}
      {showSavedConfigs && (
        <SavedConfigsBrowser
          onSelectConfig={handleLoadSavedConfig}
          onClose={() => setShowSavedConfigs(false)}
        />
      )}

      {/* Action Buttons */}
      <div className="sticky bottom-0 bg-white/80 backdrop-blur-md border-t border-slate-200 -mx-8 px-8 py-5 mt-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          {/* Secondary Actions */}
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setShowSavedConfigs(true)}
              className="btn btn-secondary"
              title="Load a previously saved configuration"
            >
              <FolderOpen className="w-4 h-4" /> Load Config
            </button>
            <button
              onClick={handleSaveConfigForReuse}
              disabled={savingConfig || Object.keys(errors).length > 0}
              className="btn btn-secondary"
              title="Save this configuration for future use"
            >
              {savingConfig ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</>
              ) : (
                <><Save className="w-4 h-4" /> Save Config</>
              )}
            </button>
            <button
              onClick={handleReset}
              className="btn btn-ghost"
            >
              Reset
            </button>
          </div>

          {/* Primary Actions */}
          <div className="flex flex-wrap gap-3">
            <button
              onClick={handlePreTrainingValidation}
              disabled={!dataFileId || saveStatus === 'saving' || analysisStatus === 'running' || isApplyingFix}
              className="btn btn-secondary"
              title={!dataFileId ? 'Data file ID required' : 'Run pre-training validation checks'}
            >
              {saveStatus === 'saving' ? 'Checking...' : isApplyingFix ? 'Fixing...' : 'Validate Data'}
            </button>
            <button
              onClick={handleRunAnalysis}
              disabled={!isValid || !dataFileId || analysisStatus === 'running'}
              className="btn btn-primary btn-lg"
              title={!dataFileId ? 'Data file ID required' : !isValid ? 'Fix validation errors first' : 'Run MMM analysis'}
            >
              <Rocket className="w-5 h-5" />
              {analysisStatus === 'running' ? 'Running Analysis...' : 'Run Analysis'}
            </button>
          </div>
        </div>
      </div>

      {/* Validation Errors Summary */}
      {Object.keys(errors).length > 0 && (
        <div className="mt-8 p-6 bg-gradient-to-r from-rose-50 to-red-50 border border-rose-200 rounded-2xl">
          <h3 className="text-rose-900 font-bold mb-4 flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-rose-100 flex items-center justify-center">
              <span className="text-rose-600 text-xs font-bold">{Object.keys(errors).length}</span>
            </div>
            Validation Errors
          </h3>
          <ul className="text-rose-800 text-sm space-y-2 max-h-48 overflow-y-auto">
            {Object.entries(errors).map(([field, message]) => (
              <li key={field} className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-rose-400 mt-2 flex-shrink-0" />
                <span><strong className="text-rose-900">{field}:</strong> {message}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
